import i18n from '@/i18n';
import { isAllowedUploadFile } from './ipeImageUploadFormats';
import { MAX_FOLDER_UPLOAD_IMAGES } from './uploadSessionLimits';

const IMAGE_NAME_PATTERN = /\.(jpe?g|png|webp)$/i;

export { MAX_FOLDER_UPLOAD_IMAGES };

/** 文件夹上传相关固定文案（与方案 §5 对齐）；取值时按当前界面语言渲染 */
export const FOLDER_UPLOAD_MESSAGES = {
  get empty() {
    return i18n.t('workbench:upload.folderEmpty');
  },
  get multiFolder() {
    return i18n.t('workbench:upload.folderMulti');
  },
  truncation: (totalMatched, max = MAX_FOLDER_UPLOAD_IMAGES) => i18n.t('workbench:upload.folderTruncation', {
    total: totalMatched,
    max,
  }),
  get dropzoneHint() {
    return i18n.t('workbench:upload.folderDropzoneHint', { max: MAX_FOLDER_UPLOAD_IMAGES });
  },
};

export function messageForFolderCollectReason(reason) {
  if (reason === 'multi_folder') return FOLDER_UPLOAD_MESSAGES.multiFolder;
  if (reason === 'empty') return FOLDER_UPLOAD_MESSAGES.empty;
  return null;
}

export function buildFolderConfirmSummary({
  folderName,
  fileCount,
  totalMatched,
  truncated,
}) {
  const name = folderName || i18n.t('workbench:upload.folderDefaultName');
  const count = Number(fileCount) || 0;
  if (truncated) {
    return i18n.t('workbench:upload.folderConfirmTruncated', {
      name,
      total: totalMatched,
      count,
    });
  }
  return i18n.t('workbench:upload.folderConfirm', { name, count });
}

export function isImageFile(file) {
  if (!file) return false;
  if (isAllowedUploadFile(file)) return true;
  if (!file.type && IMAGE_NAME_PATTERN.test(file.name || '')) return true;
  return false;
}

export function filterImageFiles(fileList) {
  return Array.from(fileList || []).filter(isImageFile);
}

/**
 * webkitRelativePath 深度：`Folder/file.jpg` → 2（根目录直属）。
 * 无 relativePath 时视为非文件夹选择结果，不纳入一层过滤。
 */
export function getWebkitRelativePathDepth(file) {
  const rel = file?.webkitRelativePath || '';
  if (!rel) return 0;
  return rel.split('/').filter(Boolean).length;
}

/** 仅保留文件夹选择结果中「根目录下一层」的图片（不含子文件夹内文件）。 */
export function filterImageFilesOneLevel(fileList) {
  return filterImageFiles(fileList).filter((file) => getWebkitRelativePathDepth(file) === 2);
}

/** 按文件名自然序（数字友好）比较，供「最前面 500 张」产品契约使用。 */
export function compareFileNamesNatural(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function sortImageFilesByNaturalName(files) {
  return [...(files || [])].sort((a, b) => compareFileNamesNatural(a?.name, b?.name));
}

/**
 * 过滤合法图片 → 自然序排序 → 截取前 max 张。
 * @returns {{ files: File[], totalMatched: number, truncated: boolean }}
 */
export function finalizeFolderImageSelection(files, options = {}) {
  const maxRaw = options.max ?? MAX_FOLDER_UPLOAD_IMAGES;
  const max = Number.isFinite(Number(maxRaw)) && Number(maxRaw) > 0
    ? Math.floor(Number(maxRaw))
    : MAX_FOLDER_UPLOAD_IMAGES;
  const sorted = sortImageFilesByNaturalName(filterImageFiles(files));
  const totalMatched = sorted.length;
  const truncated = totalMatched > max;
  return {
    files: truncated ? sorted.slice(0, max) : sorted,
    totalMatched,
    truncated,
  };
}

export function buildFolderTruncationMessage(totalMatched, max = MAX_FOLDER_UPLOAD_IMAGES) {
  return FOLDER_UPLOAD_MESSAGES.truncation(totalMatched, max);
}

/**
 * 处理 webkitdirectory input 的 FileList：仅一层 + 排序截断。
 * @returns {{ folderName: string, files: File[], totalMatched: number, truncated: boolean }}
 */
export function takeOneFolderImagesFromFileList(fileList, options = {}) {
  const list = Array.from(fileList || []);
  const folderName = list[0]?.webkitRelativePath?.split('/').filter(Boolean)[0] || i18n.t('workbench:upload.folderDefaultName');
  const oneLevel = filterImageFilesOneLevel(list);
  return {
    folderName,
    ...finalizeFolderImageSelection(oneLevel, options),
  };
}

async function readAllDirectoryEntries(dirEntry) {
  const reader = dirEntry.createReader();
  const collected = [];

  const readBatch = () => new Promise((resolve) => {
    reader.readEntries(
      (entries) => {
        if (!entries.length) {
          resolve();
          return;
        }
        collected.push(...entries);
        readBatch().then(resolve);
      },
      () => resolve(),
    );
  });

  await readBatch();
  return collected;
}

async function readFileEntryAsImage(entry) {
  if (!entry?.isFile) return null;
  return new Promise((resolve) => {
    entry.file(
      (file) => resolve(isImageFile(file) ? file : null),
      () => resolve(null),
    );
  });
}

export function isFolderPickerSupported() {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

/** 仅读取目录 handle 的直接子文件，跳过子目录。 */
async function collectFilesFromDirectoryHandleOneLevel(dirHandle) {
  const files = [];
  for await (const entry of dirHandle.values()) {
    if (entry.kind === 'file') {
      files.push(await entry.getFile());
    }
  }
  return files;
}

/**
 * 使用 File System Access API 选择 1 个文件夹，只读根目录直属图片，按文件名取前 500。
 * 无 showDirectoryPicker 时返回 supported:false，由调用方走 webkitdirectory + takeOneFolderImagesFromFileList。
 *
 * @returns {Promise<{
 *   supported: boolean,
 *   cancelled: boolean,
 *   folderName: string,
 *   files: File[],
 *   totalMatched: number,
 *   truncated: boolean,
 * }>}
 */
export async function pickOneFolderImages(options = {}) {
  if (!isFolderPickerSupported()) {
    return {
      supported: false,
      cancelled: false,
      folderName: '',
      files: [],
      totalMatched: 0,
      truncated: false,
    };
  }

  let dirHandle;
  try {
    dirHandle = await window.showDirectoryPicker({ mode: 'read' });
  } catch (error) {
    if (error?.name === 'AbortError') {
      return {
        supported: true,
        cancelled: true,
        folderName: '',
        files: [],
        totalMatched: 0,
        truncated: false,
      };
    }
    throw error;
  }

  const rawFiles = await collectFilesFromDirectoryHandleOneLevel(dirHandle);
  const finalized = finalizeFolderImageSelection(rawFiles, options);
  return {
    supported: true,
    cancelled: false,
    folderName: dirHandle.name || i18n.t('workbench:upload.folderDefaultName'),
    ...finalized,
  };
}

/**
 * 从拖拽 DataTransfer 读取「恰好 1 个文件夹」的根目录直属图片。
 * - 多个文件夹 → ok:false, reason:'multi_folder'
 * - 无文件夹（仅散文件）→ ok:false, reason:'files_only'（调用方走普通多文件逻辑）
 * - 文件夹内无合法图 → ok:false, reason:'empty'
 *
 * @returns {Promise<{
 *   ok: boolean,
 *   reason?: 'multi_folder' | 'files_only' | 'empty' | 'unavailable',
 *   folderName?: string,
 *   files: File[],
 *   totalMatched: number,
 *   truncated: boolean,
 * }>}
 */
export async function collectOneFolderImagesFromDataTransfer(dataTransfer, options = {}) {
  const emptyResult = (reason) => ({
    ok: false,
    reason,
    files: [],
    totalMatched: 0,
    truncated: false,
  });

  const items = dataTransfer?.items;
  if (!items?.length) {
    return emptyResult('unavailable');
  }

  const directoryEntries = [];

  for (let i = 0; i < items.length; i += 1) {
    const entry = items[i].webkitGetAsEntry?.();
    if (!entry) continue;
    if (entry.isDirectory) directoryEntries.push(entry);
  }

  if (directoryEntries.length > 1) {
    return emptyResult('multi_folder');
  }

  if (directoryEntries.length === 0) {
    return emptyResult('files_only');
  }

  const dirEntry = directoryEntries[0];
  const children = await readAllDirectoryEntries(dirEntry);
  const images = [];
  for (const child of children) {
    if (!child.isFile) continue;
    const image = await readFileEntryAsImage(child);
    if (image) images.push(image);
  }

  const finalized = finalizeFolderImageSelection(images, options);
  if (finalized.totalMatched === 0) {
    return {
      ok: false,
      reason: 'empty',
      folderName: dirEntry.name || i18n.t('workbench:upload.folderDefaultName'),
      files: [],
      totalMatched: 0,
      truncated: false,
    };
  }

  return {
    ok: true,
    folderName: dirEntry.name || i18n.t('workbench:upload.folderDefaultName'),
    ...finalized,
  };
}

/**
 * 拖拽入口统一分流：单文件夹 / 散文件 / 错误。
 * 须在首个 await 前同步抓取 files（Windows 多选坑）。
 *
 * @returns {Promise<
 *   | { kind: 'folder', folderName: string, files: File[], totalMatched: number, truncated: boolean }
 *   | { kind: 'files', files: File[] }
 *   | { kind: 'error', reason: string, message: string, folderName?: string }
 * >}
 */
export async function resolveFolderOrFilesDrop(dataTransfer, options = {}) {
  const syncFiles = filterImageFiles(dataTransfer?.files || []);
  const folderResult = await collectOneFolderImagesFromDataTransfer(dataTransfer, options);

  if (folderResult.ok) {
    return {
      kind: 'folder',
      folderName: folderResult.folderName,
      files: folderResult.files,
      totalMatched: folderResult.totalMatched,
      truncated: folderResult.truncated,
    };
  }

  if (folderResult.reason === 'multi_folder' || folderResult.reason === 'empty') {
    return {
      kind: 'error',
      reason: folderResult.reason,
      message: messageForFolderCollectReason(folderResult.reason),
      folderName: folderResult.folderName,
    };
  }

  return {
    kind: 'files',
    files: syncFiles,
  };
}
